// Copyright 2023, mochabug AB
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
//     http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.

import { create, fromJsonString, toBinary } from '@bufbuild/protobuf';
import { Client, ConnectError, createClient } from '@connectrpc/connect';
import { createGrpcTransport } from '@connectrpc/connect-node';
import chalk from 'chalk';
import fg from 'fast-glob';
import fs from 'fs';
import path from 'path';
import sharp from 'sharp';
import { VertexMetadataSchema } from './genproto/mochabugapis/adapt/graph/vertex_metadata_pb';
import { Manifest } from './genproto/mochabugapis/adapt/plugins/v1/manifest_pb';
import {
  PluginService,
  StartUploadSchema,
  UploadPluginRequest,
  UploadPluginRequestSchema
} from './genproto/mochabugapis/adapt/plugins/v1/plugins_pb';

async function resize(
  filepath: string,
  height: number,
  width: number
): Promise<Buffer> {
  const extname = path.extname(filepath);
  switch (extname) {
    case '.avif':
      return await sharp(filepath).resize(height, width).avif().toBuffer();
    case '.gif':
      return await sharp(filepath).resize(height, width).gif().toBuffer();
    case '.jpg':
    case '.jpeg':
    case '.jfif':
    case '.pjpeg':
    case '.pjp':
      return await sharp(filepath).resize(height, width).jpeg().toBuffer();
    case '.png':
      return await sharp(filepath).resize(height, width).png().toBuffer();
    case '.svg':
      return fs.readFileSync(filepath);
    case '.webp':
      return await sharp(filepath).resize(height, width).webp().toBuffer();
  }
  throw new Error('Invalid logotype');
}

function isPathSafe(filePath: string): boolean {
  const currentWorkingDir = path.resolve('.');
  const absoluteFilePath = path.resolve(filePath);
  return absoluteFilePath.startsWith(currentWorkingDir);
}

interface SendOpts {
  cdnFolder?: string;
  assetsFolder?: string;
  configuratorPath?: string;
  reqHeaders?: HeadersInit;
  executorPath: string;
  manifest: Manifest;
  client: Client<typeof PluginService>;
}

async function sendPlugin(opts: SendOpts) {
  // Helper function to send files
  const lookup: { [key: string]: boolean } = {};

  async function createMetadataRequest(filePath?: string) {
    if (!filePath || lookup[filePath]) {
      return;
    }
    if (!isPathSafe(filePath)) {
      throw new Error(
        `The file path is not safe: ${filePath}. It must be contained inside the manifest folder. Invalid manifest`
      );
    }
    if (!fs.existsSync(filePath)) {
      throw new Error(
        `The path does not exists: ${filePath}. Invalid manifest`
      );
    }
    const buffer = fs.readFileSync(filePath, 'utf-8');
    lookup[filePath] = true;

    return create(UploadPluginRequestSchema, {
      data: {
        case: 'file',
        value: {
          data: toBinary(
            VertexMetadataSchema,
            fromJsonString(VertexMetadataSchema, buffer)
          ),
          name: filePath
        }
      }
    });
  }

  async function createFileRequest(
    filePath?: string,
    imgOpts?: [number, number]
  ): Promise<UploadPluginRequest | undefined> {
    if (!filePath || lookup[filePath]) {
      return;
    }

    if (!isPathSafe(filePath)) {
      throw new Error(
        `The file path is not safe: ${filePath}. It must be contained inside the manifest folder. Invalid manifest`
      );
    }

    if (!fs.existsSync(filePath)) {
      throw new Error(
        `The path does not exists: ${filePath}. Invalid manifest`
      );
    }

    // If image file, we need to compress it
    let buffer: Buffer;
    if (imgOpts) {
      buffer = await resize(filePath, imgOpts[0], imgOpts[1]);
      console.log(
        `Compressed: ${filePath} to ${imgOpts[0]}x${imgOpts[1]} size`
      );
    } else {
      buffer = fs.readFileSync(filePath);
    }
    lookup[filePath] = true;
    return create(UploadPluginRequestSchema, {
      data: {
        case: 'file',
        value: {
          data: buffer,
          name: filePath
        }
      }
    });
  }

  let resTrailers: Headers = new Headers();
  let resHeaders: Headers = new Headers();
  await opts.client.uploadPlugin(
    (async function* () {
      yield create(UploadPluginRequestSchema, {
        data: {
          case: 'start',
          value: create(StartUploadSchema, {
            cdnPrefix: opts.cdnFolder,
            assetsPrefix: opts.assetsFolder,
            manifest: opts.manifest,
            executorPath: opts.executorPath,
            configuratorPath: opts.configuratorPath
          })
        }
      });

      const files: Array<Promise<UploadPluginRequest | undefined>> = [
        createFileRequest('README.md'),
        createFileRequest('LICENSE.md'),
        createFileRequest(opts.executorPath),
        createFileRequest(opts.configuratorPath),
        createFileRequest(opts.manifest.logo, [80, 80])
      ];

      if (opts.assetsFolder && fs.existsSync(opts.assetsFolder)) {
        const assetFiles = await fg(path.join(opts.assetsFolder, '**', '*'));
        assetFiles.forEach((file) => {
          files.push(createFileRequest(file));
        });
      }
      if (opts.cdnFolder && fs.existsSync(opts.cdnFolder)) {
        const cdnFiles = await fg(path.join(opts.cdnFolder, '**', '*'));
        cdnFiles.forEach((file) => {
          files.push(createFileRequest(file));
        });
      }
      for (let vertex of opts.manifest.vertices) {
        files.push(createFileRequest(vertex.logo, [40, 40]));
        files.push(createMetadataRequest(vertex.metadata));
        if (vertex.config) {
          files.push(createFileRequest(vertex.config));
        }
      }
      for await (const file of files) {
        if (file) {
          yield file;
        }
      }
    })(),
    {
      headers: opts.reqHeaders,
      onHeader: (h) => {
        resHeaders = h;
      },
      onTrailer: (h) => {
        resTrailers = h;
      }
    }
  );

  printRpcSuccess(resHeaders, resTrailers);
}

interface EmulateOpts {
  cdnFolder?: string;
  assetsFolder?: string;
  configuratorPath?: string;
  executorPath: string;
  manifest: Manifest;
  endpoint: string;
}

export async function emulate(opts: EmulateOpts) {
  const client = createClient(
    PluginService,
    createGrpcTransport({
      baseUrl: opts.endpoint
    })
  );
  try {
    await sendPlugin({ ...opts, client });
  } catch (e: any) {
    if (e instanceof ConnectError) {
      printRpcError(e);
    } else {
      console.error(e);
    }
    console.log(
      `For more information, use: ${chalk.blue('adaptkit help emulate')}`
    );
  }
}

interface PublishOpts {
  cdnFolder?: string;
  assetsFolder?: string;
  configuratorPath?: string;
  executorPath: string;
  manifest: Manifest;
  endpoint: string;
  accessToken: string;
  insecure: boolean;
}

export async function publish(opts: PublishOpts) {
  const client = createClient(
    PluginService,
    createGrpcTransport({
      baseUrl: opts.endpoint
    })
  );
  const metadata: Headers = new Headers({
    authorization: `Bearer ${opts.accessToken}`
  });
  try {
    await sendPlugin({
      ...opts,
      client,
      reqHeaders: metadata
    });
  } catch (e: any) {
    if (e instanceof ConnectError) {
      printRpcError(e);
    } else {
      console.error(e);
    }
    console.log(
      `For more information, use: ${chalk.blue('adaptkit help publish')}`
    );
  }
}

function printRpcSuccess(headers: Headers, trailers: Headers) {
  const line = chalk.green(
    '=================================================='
  );
  const title = chalk.bgGreen.black.bold(' RPC Success Details ');
  const statusCode =
    chalk.bold.white('Status Code: ') + chalk.greenBright.bold('OK');
  const statusDetail =
    chalk.bold.white('Details: ') +
    chalk.greenBright('The upload was successful');

  const headerInfo =
    chalk.bold.white('Headers:\n') +
    (headers
      ? chalk.blue(JSON.stringify(headers, null, 2))
      : chalk.gray('  No Metadata'));

  const trailerInfo =
    chalk.bold.white('Trailers:\n') +
    (trailers
      ? chalk.blue(JSON.stringify(trailers, null, 2))
      : chalk.gray('  No Trailers'));

  console.log(line);
  console.log(title);
  console.log(line);
  console.log(statusCode);
  console.log(statusDetail);
  console.log(line);
  console.log(headerInfo);
  console.log(line);
  console.log(trailerInfo);
  console.log(line);
  console.log(chalk.bgGreen.black.bold('        SUCCESS        '));
  console.log(line);
}

function printRpcError(error: ConnectError) {
  const line = chalk.red('==================================================');
  const title = chalk.bgRed.white.bold(' RPC Error Details ');
  const name = chalk.bold.white('Name: ') + chalk.redBright.bold(error.name);
  const message =
    chalk.bold.white('Message: ') + chalk.redBright(error.message);
  const code = chalk.bold.white('Code: ') + chalk.redBright.bold(error.code);

  const methodName = (error as any).methodName
    ? chalk.bold.white('Method Name: ') +
      chalk.yellowBright((error as any).methodName)
    : chalk.gray('No Method Name');

  const serviceName = (error as any).serviceName
    ? chalk.bold.white('Service Name: ') +
      chalk.yellowBright((error as any).serviceName)
    : chalk.gray('No Service Name');

  const meta =
    chalk.bold.white('Metadata:\n') +
    (error.metadata
      ? chalk.blue(JSON.stringify([...error.metadata.entries()], null, 2))
      : chalk.gray('  No Metadata'));

  console.error(line);
  console.error(title);
  console.error(line);
  console.error(name);
  console.error(message);
  console.error(code);
  console.error(line);
  if (methodName) console.error(methodName);
  if (serviceName) console.error(serviceName);
  console.error(line);
  console.error(meta);
  console.error(line);

  const details = error.details;
  console.error(chalk.bold.white('Error Details:'));
  if (details.length === 0) {
    console.error(chalk.gray('  No error details'));
  } else {
    for (const detail of details) {
      console.error(chalk.red(JSON.stringify(detail, null, 2)));
    }
  }

  console.error(line);
  console.error(chalk.bgRed.white.bold('        ERROR        '));
  console.error(line);
}
